import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';

/**
 * Trusted-device id used by the 2FA "remember this device" flow.
 *
 * The backend used to identify the device with an httpOnly `deviceId` cookie, but
 * white-brand apps run on a different domain than the API, so browsers drop it as a
 * third-party cookie and the device was never remembered. The id now travels through
 * the `X-Device-Id` header and is persisted here.
 *
 * The value is scoped per API environment: a device trusted against one backend means
 * nothing to another.
 *
 * Security note: the id is reachable from JavaScript, unlike the cookie it replaces.
 * That is an accepted trade-off — on its own it grants nothing, it only skips the
 * second factor when presented together with a valid JWT. Never log its value.
 */
@Injectable({
  providedIn: 'root',
})
export class DeviceIdService {
  static readonly HEADER = 'X-Device-Id';

  private readonly storageKey = `deviceId::${environment.apiUrl}`;

  get(): string | null {
    return localStorage.getItem(this.storageKey) || null;
  }

  set(deviceId: string): void {
    if (!deviceId) {
      return;
    }
    localStorage.setItem(this.storageKey, deviceId);
  }

  /** Only for an explicit device revocation by the user — never on logout. */
  clear(): void {
    localStorage.removeItem(this.storageKey);
  }

  /**
   * Replacement for a bare `localStorage.clear()`. The trusted-device id has to outlive
   * the session: wiping it on logout makes the next login register a brand new device
   * and challenge the user for 2FA again, which is the exact bug this flow fixes.
   */
  clearStorageKeepingDeviceId(): void {
    const deviceId = this.get();
    localStorage.clear();
    if (deviceId) {
      this.set(deviceId);
    }
  }
}
